import React from 'react';
import { Calendar, Users, Target, Wallet, TrendingUp, TrendingDown } from 'lucide-react';
import { DashboardStatsResponse } from '@clareefai/types';
import { useTranslation } from 'react-i18next';
import { useGetVendorBalanceQuery } from '../../store/vendorApi';
import StatCard from '../shared/StatCard';

interface DashboardStatsProps {
  stats: DashboardStatsResponse | undefined;
  loading: boolean;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ stats, loading }) => {
  const { t } = useTranslation();
  const { data: balanceData, isLoading: balanceLoading } = useGetVendorBalanceQuery();

  if (loading || balanceLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white p-5 rounded-2xl shadow-sm ring-1 ring-gray-900/[0.04] animate-pulse">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="w-11 h-11 bg-gray-200 rounded-xl"></div>
            </div>
            <div className="h-5 bg-gray-200 rounded-full w-2/3 mt-4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const formatChange = (change: number) => {
    const positive = change >= 0;
    const sign = positive ? '+' : '';
    const TrendIcon = positive ? TrendingUp : TrendingDown;
    const pill = positive
      ? 'bg-green-50 text-green-700'
      : 'bg-red-50 text-red-700';
    return (
      <span className="flex items-center gap-1.5 text-xs whitespace-nowrap">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold tabular-nums ${pill}`}
        >
          <TrendIcon className="w-3 h-3 shrink-0" />
          {t('dashboard.stats.trendValue', { sign, value: change.toFixed(1) })}
        </span>
        <span className="text-gray-400 truncate">
          {t('dashboard.stats.vsLastMonthShort')}
        </span>
      </span>
    );
  };

  const displayStats = [
    {
      title: t('dashboard.totalMeetings'),
      value: stats.totalMeetings,
      formatValue: (v: number) => Math.round(v).toLocaleString(),
      icon: Calendar,
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      change: stats.monthlyTrends?.meetings ? formatChange(stats.monthlyTrends.meetings.change) : null,
      to: '/meetings'
    },
    {
      title: t('dashboard.activePromoters'),
      value: stats.activePromoters,
      formatValue: (v: number) => Math.round(v).toLocaleString(),
      icon: Users,
      color: 'bg-gradient-to-r from-green-500 to-emerald-600',
      change: stats.monthlyTrends?.activePromoters ? formatChange(stats.monthlyTrends.activePromoters.change) : null,
      to: '/promoters'
    },
    {
      title: t('dashboard.prospects'),
      value: stats.totalProspects,
      formatValue: (v: number) => Math.round(v).toLocaleString(),
      icon: Target,
      color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      change: stats.monthlyTrends?.prospects ? formatChange(stats.monthlyTrends.prospects.change) : null,
      to: '/prospects'
    },
    {
      title: t('dashboard.stats.balance'),
      value: balanceData?.balance || 0,
      formatValue: (v: number) => `${v.toFixed(2)}€`,
      icon: Wallet,
      color: 'bg-gradient-to-r from-orange-500 to-red-500',
      change: null,
      to: '/rewards'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {displayStats.map((stat, index) => (
        <StatCard
          key={index}
          index={index}
          title={stat.title}
          value={stat.value}
          formatValue={stat.formatValue}
          icon={stat.icon}
          color={stat.color}
          change={stat.change}
          to={stat.to}
        />
      ))}
    </div>
  );
};

export default DashboardStats;